import type { DashboardTask } from "@dune/types";
import { formatSimpleDate } from "@dune/utils";
import {
	differenceInDays,
	isPast,
	isToday,
	isTomorrow,
	startOfDay,
} from "date-fns";
import { TaskStatusIcon } from "./TaskStatusIcon";

interface TaskDueDateProps {
	task: Pick<DashboardTask, "status" | "dueDate" | "completedAt">;
	showIcon?: boolean;
	variant?: "full" | "compact";
}

export const TaskDueDate: React.FC<TaskDueDateProps> = ({
	task,
	showIcon = true,
	variant = "full",
}) => {
	const getDueDateDisplay = () => {
		if (task.status === "DONE") {
			const formattedDate = task.dueDate ? formatSimpleDate(task.dueDate) : "-";

			if (
				task.dueDate &&
				task.completedAt &&
				new Date(task.completedAt) > new Date(task.dueDate)
			) {
				return {
					text: formattedDate,
					colorClass: "text-orange-600",
				};
			}
			return {
				text: formattedDate,
				colorClass: "text-green-600",
			};
		}

		if (!task.dueDate) {
			return {
				text: "-",
				colorClass: "text-gray-500",
			};
		}

		const dueDateObj = new Date(task.dueDate);
		const formattedDate = formatSimpleDate(dueDateObj);

		// Check if date is in the past (comparing start of days)
		if (isPast(startOfDay(dueDateObj)) && !isToday(dueDateObj)) {
			if (variant === "full") {
				const daysLate = Math.abs(differenceInDays(new Date(), dueDateObj));
				return {
					text: `${formattedDate} (${daysLate}j de retard)`,
					colorClass: "text-red-600 font-medium",
				};
			}
			return {
				text: formattedDate,
				colorClass: "text-red-600",
			};
		}

		if (variant === "full") {
			if (isToday(dueDateObj)) {
				return {
					text: `${formattedDate} (aujourd'hui)`,
					colorClass: "text-orange-600 font-medium",
				};
			}
			if (isTomorrow(dueDateObj)) {
				return {
					text: `${formattedDate} (demain)`,
					colorClass: "text-orange-600 font-medium",
				};
			}

			const daysRemaining = differenceInDays(dueDateObj, new Date());
			if (daysRemaining > 1 && daysRemaining <= 7) {
				return {
					text: `${formattedDate} (${daysRemaining}j restants)`,
					colorClass: "text-blue-600",
				};
			}
		}

		return {
			text: formattedDate,
			colorClass: "text-green-600",
		};
	};

	const { text, colorClass } = getDueDateDisplay();

	return (
		<div className="flex items-center gap-2 text-sm">
			{showIcon && <TaskStatusIcon task={task} showTooltip={false} />}
			<span className={colorClass}>{text}</span>
		</div>
	);
};
